import { Component, ElementRef, Inject, LOCALE_ID, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router/';
import { interval, Subscription } from 'rxjs';
import { format } from 'date-fns';
import { Chart } from 'chart.js';

declare var $: any;
declare var Plotly: any;

import {
    CalculationInput, CalculationModelNames, CalculationModelType,
    CalculationStatus, CalculationType, CommentCategory,
    CommentCategoryHeaderText, Degradation, PointResult, QualityCriterionType, RiskCalculationDetailView
} from 'src/app/contracts';

import { CalculationService, CsvService, PdfmakeService } from 'src/app/services';
import { CsvDataHelper } from './csv-data.helper';

const win = window as any;

@Component({
    selector: 'app-calculation-result',
    templateUrl: './calculation-result.component.html',
    styleUrls: ['./calculation-result.component.css']
})
export class CalculationResultComponent implements OnInit, OnDestroy {
    @ViewChild('pdfCalculationHeaderDiv') public pdfCalculationHeaderDiv: ElementRef;
    // @ViewChild('pdfCalculationParametersDiv') private pdfCalculationParametersDiv: ElementRef;
    @ViewChild('pdfForureningskildeParametersDiv') public pdfForureningskildeParametersDiv: ElementRef;
    @ViewChild('pdfVerticalTransportParametersDiv') public pdfVerticalTransportParametersDiv: ElementRef;
    @ViewChild('pdfHorizontalTransportParametersDiv') public pdfHorizontalTransportParametersDiv: ElementRef;
    @ViewChild('pdfKontrolpunkterParametersDiv') public pdfKontrolpunkterParametersDiv: ElementRef;
    @ViewChild('pdfResultatForKontrolpunkt') public pdfResultatForKontrolpunkt: ElementRef;
    @ViewChild('pdfResultatForValgtKontrolpunkt') public pdfResultatForValgtKontrolpunkt: ElementRef;

    @ViewChild('pdfCalculationResultsDiv') public pdfCalculationResultsDiv: ElementRef;
    @ViewChild('pdfPlumeConcentrationPlotDiv') public pdfPlumeConcentrationPlotDiv: ElementRef;
    @ViewChild('pdfPlumeConcentrationAtPoCPlotDiv') public pdfPlumeConcentrationAtPoCPlotDiv: ElementRef;
    @ViewChild('pdfConcentrationPlotDiv') public pdfConcentrationPlotDiv: ElementRef;
    @ViewChild('pdfAquiferTopConcentrationPlotDiv') public pdfAquiferTopConcentrationPlotDiv: ElementRef;

    @ViewChild('HorizontalPlumeConcentrationPlotDiv') public horizontalPlumeConcentrationPlotDiv: ElementRef;
    @ViewChild('PlumeConcentrationAtPoCPlotDiv') public plumeConcentrationAtPoCPlotDiv: ElementRef;
    @ViewChild('VerticalConcentrationPlotDiv') public verticalConcentrationPlotDiv: ElementRef;
    @ViewChild('AquiferTopConcentrationPlotDiv') public aquiferTopConcentrationPlotDiv: ElementRef;

    @ViewChild('HorizontalPlumeConcentrationPlotContainer') public horizontalPlumeConcentrationPlotContainer: ElementRef;
    @ViewChild('PlumeConcentrationAtPoCPlotContainer') public plumeConcentrationAtPoCPlotContainer: ElementRef;
    @ViewChild('VerticalConcentrationPlotDivContainer') public verticalConcentrationPlotContainer: ElementRef;
    @ViewChild('AquiferTopConcentrationPlotContainer') public aquiferTopConcentrationPlotContainer: ElementRef;

    public calculationId: string;
    public calculationLoading = true;
    public calculationFailed: boolean;
    public calculationFailedErrorMessage: string;
    public tick: Subscription;
    public calculation: RiskCalculationDetailView;
    public calculationComplete = false;
    public hasLoadingError = false;
    public calculationInProgress = false;
    public calculationIsApproved = false;
    public calculationInput: CalculationInput;
    public pointResults: PointResult[];
    public degradationCompoundIndices: number[];
    public plotPointResult: PointResult;
    public plotPointDist: number;
    public horizontalPlumeConcentrationLegend: any[];
    public plumeConcentrationAtPoCPlotLegend: any[];
    public verticalConcentrationPlotLegend: any[];
    public aquiferTopConcentrationPlotLegend: any[];

    public horizontalPlumeConcentrationChart: Chart;
    public plumeConcentrationAtPoCChart: Chart;
    public verticalModelOutputChart: Chart;

    // Helpers to get access to enums in the template html
    public calculationModelType = CalculationModelType;
    public degradation = Degradation;
    public calculationModelNames = CalculationModelNames;
    public calculationStatus = CalculationStatus;
    public commentCategoryHeaderText = CommentCategoryHeaderText;
    public calculationType = CalculationType;
    public qualityCriterionType = QualityCriterionType;

    public commentText: string;

    public canRequestCancel: boolean = false;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly calculationService: CalculationService,
        private readonly pdfmake: PdfmakeService,
        private readonly csvService: CsvService,
        @Inject(LOCALE_ID) private readonly locale
    ) {
    }

    public ngOnInit(): void {
        this.calculationId = this.route.snapshot.paramMap.get('id');
        this.loadCalculationDetails();
    }

    public ngOnDestroy(): void {
        if (this.tick) {
            this.tick.unsubscribe();
        }
    }

    private loadCalculationDetails(): void {
        this.calculationLoading = true;
        this.canRequestCancel = false;
        this.calculationService.getCalculationDetails(this.calculationId).subscribe(
            res => this.calculationReceived(res),
            error => {
                this.hasLoadingError = true;
                this.calculationLoading = false;
                this.canRequestCancel = true;
            },
        );
    }

    public newCalculation(): void {
        this.calculationService.createTempFromCalculation(
            this.calculation.location.id, this.calculation.location.name,
            this.calculation.location.number,
            this.calculation.calculationType, this.calculationInput);
        this.router.navigate(['riskassessment/select-parameters']);
    }

    public pointResultIsRisk(index: number): boolean {
        const isRiskList: boolean[] = [];
        if (!this.calculationComplete || !this.pointResults) {
            return false;
        }

        const degradationChainCompoundNames = this.calculationInput.degradationChainCompoundNames;
        for (const compoundName of degradationChainCompoundNames) {
            // ignore compound names are empty or null or undefined.
            if (!compoundName) {
                continue;
            }

            const compoundNameIndex = degradationChainCompoundNames.indexOf(compoundName);
            const isRisk = this.pointResults[index].excessFactor[compoundNameIndex] >= 1.0;
            isRiskList.push(isRisk);
        }
        return isRiskList.some(isRisk => isRisk === true);
    }

    public approve(comment: string): void {
        this.calculationService.approveCalculation(this.calculation.id, comment).subscribe(res => {
            this.loadCalculationDetails();
        });
    }

    public unapprove(comment: string): void {
        this.calculationService.unapproveCalculation(this.calculation.id, comment).subscribe(res => {
            this.loadCalculationDetails();
        });
    }

    public addComment(): void {
        this.calculationService.addCommentToCalculation(this.calculationId, this.commentText).subscribe(res => {
            this.commentText = '';
            this.loadCalculationDetails();
        });
    }

    // Sets up plots
    public calculationReceived(calculation: RiskCalculationDetailView): void {
        this.canRequestCancel = true;
        this.calculation = calculation;
        this.calculationInput = this.calculation.calculationInput;
        this.degradationCompoundIndices = Array<number>(this.calculation.calculationInput.compoundCount).fill(0).map((x, i) => i);
        this.calculationComplete = this.calculation.calculationStatus === CalculationStatus.AwaitingApproval
            || this.calculation.calculationStatus === CalculationStatus.Approved;
        this.calculationInProgress = !this.calculationComplete;
        this.calculationIsApproved = this.calculation.calculationStatus === CalculationStatus.Approved;
        if (calculation.calculationFailed) {
            this.calculationLoading = false;
            this.calculationFailed = true;
            this.calculationFailedErrorMessage = calculation.calculationFailedErrorMessage;
            return;
        }

        if (this.calculationInProgress) {
            // Recheck calculation every 20 seconds
            this.tick = interval(20 * 1000).subscribe(tick => {
                this.calculationService.getCalculationDetails(this.calculationId).subscribe(res => {
                    if (!res.calculationFailed) {
                        if (!res.calculationResult) {
                            return;
                        }

                        this.calculation.calculationResult = res.calculationResult;
                        this.calculation.calculationFailed = res.calculationFailed;
                        this.calculation.calculationFailedErrorMessage = res.calculationFailedErrorMessage;
                        this.fillPlotData();
                        this.calculationComplete = true;
                        this.calculationInProgress = false;

                        this.tick.unsubscribe();
                        this.tick = null;
                    } else {
                        this.calculationInProgress = false;
                        this.calculationLoading = false;
                        this.calculationFailed = res.calculationFailed;
                        this.calculationFailedErrorMessage = res.calculationFailedErrorMessage;

                        this.tick.unsubscribe();
                        this.tick = null;
                    }
                });
            });
        }

        this.calculationLoading = false;

        const intervalRef = setInterval(() => {
            clearInterval(intervalRef);
            if (this.calculationComplete) {
                this.fillPlotData();
            }
        });

    }

    public shortCompoundNameFromDegradationChain(index: number): string {
        return (this.calculationInput.degradationChainCompoundNames[index].length > 10)
            ? (this.calculationInput.degradationChainCompoundNames[index].substring(0, 8) + '...')
            : this.calculationInput.degradationChainCompoundNames[index];
    }

    public onResize(event): void {
        try {
            // Resize plot graphs (not handled by plotly!!)
            Plotly.relayout(this.horizontalPlumeConcentrationPlotDiv.nativeElement, {
                width: this.horizontalPlumeConcentrationPlotContainer.nativeElement.offsetWidth
            });

            Plotly.relayout(this.plumeConcentrationAtPoCPlotDiv.nativeElement, {
                width: this.plumeConcentrationAtPoCPlotContainer.nativeElement.offsetWidth
            });

            Plotly.relayout(this.verticalConcentrationPlotDiv.nativeElement, {
                width: this.verticalConcentrationPlotContainer.nativeElement.offsetWidth
            });

            Plotly.relayout(this.aquiferTopConcentrationPlotDiv.nativeElement, {
                width: this.aquiferTopConcentrationPlotContainer.nativeElement.offsetWidth
            });
        } catch (ex) {
            console.warn(ex);
        }
    }

    public hotCancelCalculation(): void {
        if (!this.calculation.id || !this.calculation?.location.id) {
            return;
        }
        this.canRequestCancel = false;

        this.calculationService.deleteCalculation(this.calculation.id).subscribe((res: any) => {
            var url;
            switch(this.calculation?.calculationType) {
                case CalculationType.PreliminaryCalculation:
                    url = "riskassessment/preliminary/location-details/"
                    break;
                case CalculationType.V1AsV2Calculation:
                case CalculationType.Standard:
                    url = "riskassessment/location-details/"
                    break;
                default:
                    break;
            }

            if (url && this.calculation?.location.id) {
                this.router.navigate([url + this.calculation?.location.id]);
            }

        });
    }

    public toggleGraph(graph: any): void {
        graph.isActive = !graph.isActive;
        graph.data.visible = graph.isActive;

        this.onResize(null);
    }

    public fillPlotData(): void {
        Chart.defaults.global.defaultFontFamily = 'Lato, sans-serif';
        this.pointResults = this.calculation.calculationResult.pointResults;
        this.plotPointResult = this.pointResults[0];
        this.plotPointDist = 100;

        if (this.calculation.calculationInput.hasAdditionalPoC) {
            this.plotPointResult = this.pointResults[1];
            this.plotPointDist = this.calculation.calculationInput.additionalPoCDistanceFromSource;
        }
        const colors = ['#43B870', '#DD7DAD', '#524D94', '#FF9783', '#70B3F8'];

        if (this.plotPointResult.horizontalPlumeConcentration) {
            const horizontalPlumeConcentrationPlotDiv = this.horizontalPlumeConcentrationPlotDiv.nativeElement;
            // filter out negative plotData.x, plotData.y in a pair
            const positiveHorzPlumeConcentration = [...this.plotPointResult.horizontalPlumeConcentration];
            for (const [indexPlot, plotData] of positiveHorzPlumeConcentration.entries()) {
                for (const [index, value] of plotData.x.entries()) {
                    if (value < 0) {
                        delete positiveHorzPlumeConcentration[indexPlot].x[index];
                        delete positiveHorzPlumeConcentration[indexPlot].y[index];
                    }
                }
                positiveHorzPlumeConcentration[indexPlot].x = positiveHorzPlumeConcentration[indexPlot].x.filter(x => !Number.isNaN(x));
                positiveHorzPlumeConcentration[indexPlot].y = positiveHorzPlumeConcentration[indexPlot].y.filter(y => !Number.isNaN(y));
            }

            const horzPlumeConcData = positiveHorzPlumeConcentration
                .map((plotData, index) =>
                    (
                        {
                            x: plotData.x,
                            y: plotData.y,
                            type: 'scatter', mode: 'lines',
                            name: this.shortCompoundNameFromDegradationChain(index),
                            marker: {
                                color: colors[index % colors.length],
                                width: 1
                            },
                            visible: true
                        }));
            const horzPlumeConcLayout = {
                autosize: false,
                width: this.horizontalPlumeConcentrationPlotContainer.nativeElement.offsetWidth, height: 300,
                margin: { l: 75, r: 35, b: 75, t: 20, pad: 4 },
                separators: ',.',
                xaxis: {
                    title: 'Afstand langs fanen (m)',
                    showline: true,
                    zeroline: true,
                    exponentformat: 'power',
                    showexponent: 'all',
                    rangemode: 'nonnegative',
                    autorange: true,
                },
                yaxis: {
                    title: 'Koncentration (µg/l)',
                    showline: true,
                    exponentformat: 'power',
                    showexponent: 'all',
                    tickangle: 45
                },
                showlegend: false,
                legend: {
                    x: 1,
                    y: 0.5
                },
                font: {
                    family: 'Lato, sans-serif',
                    size: 11,
                    color: '#4f4f4f'
                }
            };
            Plotly.newPlot(horizontalPlumeConcentrationPlotDiv, horzPlumeConcData, horzPlumeConcLayout, { displayModeBar: false });

            this.horizontalPlumeConcentrationLegend = this.plotPointResult.horizontalPlumeConcentration
                .map((plotData, index) =>
                    ({
                        name: this.shortCompoundNameFromDegradationChain(index),
                        toolTip: this.calculationInput.degradationChainCompoundNames[index],
                        color: colors[index % colors.length],
                        isActive: true,
                        data: horzPlumeConcData[index],
                        element: this.horizontalPlumeConcentrationPlotDiv.nativeElement,
                        index
                    }));
        }

        if (this.plotPointResult.plumeConcentrationAtPoC) {
            const plumeConcentrationAtPoCPlotDiv = this.plumeConcentrationAtPoCPlotDiv.nativeElement;
            const plumeConcentrationAtPoCData = this.plotPointResult.plumeConcentrationAtPoC
                .map((plotData, index) =>
                    ({
                        x: plotData.x,
                        y: plotData.y,
                        type: 'scatter', mode: 'lines',
                        name: this.shortCompoundNameFromDegradationChain(index),
                        marker: {
                            color: colors[index % colors.length],
                            width: 1
                        }
                    }));
            const plumeConcentrationAtPoCLayout = {
                autosize: false,
                width: this.plumeConcentrationAtPoCPlotContainer.nativeElement.offsetWidth, height: 300,
                margin: { l: 75, r: 35, b: 75, t: 20, pad: 4 },
                separators: ',.',
                xaxis: {
                    title: 'Koncentration (µg/L)',
                    showline: true,
                    zeroline: true,
                    exponentformat: 'power',
                    showexponent: 'all'
                },
                yaxis: {
                    title: 'Dybde under grundvandsspejlet (m)',
                    showline: true,
                    exponentformat: 'power',
                    showexponent: 'all'
                },
                showlegend: false,
                legend: {
                    x: 1,
                    y: 0.5
                },
                font: {
                    family: 'Lato, sans-serif',
                    size: 11,
                    color: '#4f4f4f'
                }
            };
            Plotly.newPlot(plumeConcentrationAtPoCPlotDiv, plumeConcentrationAtPoCData,
                plumeConcentrationAtPoCLayout, { displayModeBar: false });

            this.plumeConcentrationAtPoCPlotLegend = this.plotPointResult.plumeConcentrationAtPoC
                .map((plotData, index) =>
                    ({
                        name: this.shortCompoundNameFromDegradationChain(index),
                        toolTip: this.calculationInput.degradationChainCompoundNames[index],
                        color: colors[index % colors.length],
                        isActive: true,
                        data: plumeConcentrationAtPoCData[index],
                        element: this.plumeConcentrationAtPoCPlotDiv.nativeElement,
                        index
                    }));
        }

        if (this.plotPointResult.verticalModelOutput) {
            const verticalConcentrationPlotDiv = this.verticalConcentrationPlotDiv.nativeElement;
            const verticalConcentrationData = this.plotPointResult.verticalModelOutput
                .map((plotData, index) =>
                    ({
                        x: plotData.x != null ? plotData.x.slice(2) : plotData.x,
                        y: plotData.y != null ? plotData.y.slice(2) : plotData.y,
                        type: 'scatter', mode: 'lines',
                        name: this.shortCompoundNameFromDegradationChain(index),
                        marker: {
                            color: colors[index % colors.length],
                            width: 1
                        }
                    }));

            const verticalConcentrationLayout = {
                autosize: false,
                width: this.verticalConcentrationPlotContainer.nativeElement.offsetWidth, height: 300,
                margin: { l: 75, r: 35, b: 75, t: 20, pad: 4 },
                separators: ',.',
                xaxis: {
                    title: 'Koncentration (µg/L)',
                    showline: true,
                    zeroline: true,
                    exponentformat: 'power',
                    showexponent: 'all'
                },
                yaxis: {
                    title: 'Dybde under kilden (m)',
                    showline: true,
                    exponentformat: 'power',
                    showexponent: 'all',
                },
                showlegend: false,
                legend: {
                    x: 1,
                    y: 0.5
                },
                font: {
                    family: 'Lato, sans-serif',
                    size: 11,
                    color: '#4f4f4f'
                }
            };
            Plotly.newPlot(verticalConcentrationPlotDiv, verticalConcentrationData, verticalConcentrationLayout, { displayModeBar: false });

            this.verticalConcentrationPlotLegend = this.plotPointResult.verticalModelOutput
                .map((plotData, index) =>
                    ({
                        name: this.shortCompoundNameFromDegradationChain(index),
                        toolTip: this.calculationInput.degradationChainCompoundNames[index],
                        color: colors[index % colors.length],
                        isActive: true,
                        data: verticalConcentrationData[index],
                        element: this.verticalConcentrationPlotDiv.nativeElement,
                        index
                    }));
        }

        if (this.plotPointResult.aquiferTopConcentration) {
            const aquiferTopConcentrationPlotDiv = this.aquiferTopConcentrationPlotDiv.nativeElement;
            const aquiferTopConcentrationData = this.plotPointResult.aquiferTopConcentration
                .map((plotData, index) =>
                    ({
                        x: plotData.x,
                        y: plotData.y,
                        type: 'scatter', mode: 'lines',
                        name: this.shortCompoundNameFromDegradationChain(index),
                        marker: {
                            color: colors[index % colors.length],
                            width: 1
                        }
                    }));

            // The plot comes from computation as two distinct parts, starting at zero but with the positive section first
            // this will be alivieated by reshuffling the points
            // tslint:disable-next-line: prefer-for-of
            for (let index = 0; index < aquiferTopConcentrationData.length; index++) {
                if (!aquiferTopConcentrationData[index].x || !aquiferTopConcentrationData[index].y) {
                    continue;
                }
                const xTail = aquiferTopConcentrationData[index].x.splice(
                    (aquiferTopConcentrationData[index].x.length) / 2,
                    (aquiferTopConcentrationData[index].x.length) / 2);
                const yTail = aquiferTopConcentrationData[index].y.splice(
                    (aquiferTopConcentrationData[index].y.length) / 2,
                    (aquiferTopConcentrationData[index].y.length) / 2);

                aquiferTopConcentrationData[index].x = xTail.reverse().concat(aquiferTopConcentrationData[index].x);
                aquiferTopConcentrationData[index].y = yTail.reverse().concat(aquiferTopConcentrationData[index].y);
            }

            const aquiferTopConcentrationLayout = {
                autosize: false,
                width: this.aquiferTopConcentrationPlotContainer.nativeElement.offsetWidth, height: 300,
                margin: { l: 75, r: 35, b: 75, t: 20, pad: 4 },
                separators: ',.',
                xaxis: {
                    title: 'Afstand fra centrum af kilden (m)',
                    showline: true,
                    zeroline: false,
                    exponentformat: 'power',
                    showexponent: 'all'
                },
                yaxis: {
                    title: 'Koncentration (µg/L)',
                    showline: true,
                    exponentformat: 'power',
                    showexponent: 'all'
                },
                showlegend: false,
                legend: {
                    x: 1,
                    y: 0.5
                },
                font: {
                    family: 'Lato, sans-serif',
                    size: 11,
                    color: '#4f4f4f'
                }
            };

            Plotly.newPlot(aquiferTopConcentrationPlotDiv, aquiferTopConcentrationData,
                aquiferTopConcentrationLayout, { displayModeBar: false });

            this.aquiferTopConcentrationPlotLegend = this.plotPointResult.aquiferTopConcentration
                .map((plotData, index) =>
                    ({
                        name: this.shortCompoundNameFromDegradationChain(index),
                        toolTip: this.calculationInput.degradationChainCompoundNames[index],
                        color: colors[index % colors.length],
                        isActive: true,
                        data: aquiferTopConcentrationData[index],
                        element: this.aquiferTopConcentrationPlotDiv.nativeElement,
                        index
                    }));
        }

        const intervalRef = setInterval(() => {
            clearInterval(intervalRef);
            if (this.calculationComplete) {
                this.onResize(null);
            }
        });
    }

    public getValidNumber(val: number): number {
        const result = (!val || isNaN(val) || !Number.isFinite(val)) ? 0 : val;
        return result;
    }

    public getNumberWithSignificantDigits(val: number): string {
        const n = this.getValidNumber(val);
        let digitsInfo = '';
        if (n >= 10.0) { // xx.x
            digitsInfo = '1.1-1';
        }
        else if (n >= 1.0) { // x.xx
            digitsInfo = '1.2-2';
        }
        else if (n >= 0.1) { // 0.xxx
            digitsInfo = '1.3-3';
        }
        else if (n >= 0.01) { // 0.0xxx
            digitsInfo = '1.4-4';
        }
        else { // 0.00xxx
            digitsInfo = '1.5-5';
        }

        const result = new DecimalPipe(this.locale).transform(n, digitsInfo);
        return result;
    }

    public downloadPdf(): void {
        let calculation: any = this.calculation;
        if (!calculation) {
            calculation = {};
        }

        this.pdfmake.initialize();

        // Configure text styles
        const styles = {
            header: {
                fontSize: 35,
                bold: true,
                color: '#444451'
            },
            subheader: {
                fontSize: 12,
                color: '#444451',
            },
            footer: {
                fontSize: 25,
                bold: true,
                color: 'blue'
            },
            labelHeader: {
                color: '#444451',
                fontSize: 14,
                italics: true,
                margin: [0, 25, 0, 0]
            },
            contentHeader: {
                fontSize: 16,
                color: '#444451',
                // background: '#66667A',
                bold: true,
                italics: true,
                margin: [0, 0, 0, 25]
            },
            commentLabel: {
                fontSize: 12,
                color: '#444451',
                italics: true,
                margin: [0, 25, 0, 0]
            },
            commentText: {
                fontSize: 10,
                color: '#444451',
                margin: [0, 15, 0, 0]
            },
            seperateLine: {
                margin: [0, 10, 0, 0]
            }
        };
        this.pdfmake.configureStyles(styles);

        // Add header
        const headerCols = {
            columns: [
                {
                    width: '*',
                    stack: [
                        { text: 'GrundRisk - Resultater', style: 'header' },
                        { text: 'Danmarks Miljøportal', style: 'subheader' },
                    ]
                },
                {
                    width: 'auto',
                    text: format(new Date(), 'dd/MM/yyyy'),
                    margin: [0, 20, 0, 0]
                },

            ]
        };
        this.pdfmake.addDynamic(headerCols);

        this.pdfmake.addText(`Lokalitet: ${this.calculation.location.number}`, 'labelHeader');
        this.pdfmake.addText(this.calculation.location.name, 'contentHeader');

        $('#modalParameterListView').css('opacity', '0.0');
        const modal = $('#toggleParameterListViewButton');
        modal.click();

        setTimeout(() => {
            const elements = [];
            if (this.pdfCalculationHeaderDiv && !this.pdfCalculationHeaderDiv.nativeElement.hidden) {
                elements.push(this.pdfCalculationHeaderDiv.nativeElement);
            }
            if (this.pdfForureningskildeParametersDiv && !this.pdfForureningskildeParametersDiv.nativeElement.hidden) {
                elements.push(this.pdfForureningskildeParametersDiv.nativeElement);
            }
            if (this.pdfVerticalTransportParametersDiv && !this.pdfVerticalTransportParametersDiv.nativeElement.hidden) {
                elements.push(this.pdfVerticalTransportParametersDiv.nativeElement);
            }
            if (this.pdfHorizontalTransportParametersDiv && !this.pdfHorizontalTransportParametersDiv.nativeElement.hidden) {
                elements.push(this.pdfHorizontalTransportParametersDiv.nativeElement);
            }
            if (this.pdfKontrolpunkterParametersDiv && !this.pdfKontrolpunkterParametersDiv.nativeElement.hidden) {
                elements.push(this.pdfKontrolpunkterParametersDiv.nativeElement);
            }
            if (this.pdfResultatForKontrolpunkt && !this.pdfResultatForKontrolpunkt.nativeElement.hidden) {
                elements.push(this.pdfResultatForKontrolpunkt.nativeElement);
            }
            if (this.pdfResultatForValgtKontrolpunkt && !this.pdfResultatForValgtKontrolpunkt.nativeElement.hidden) {
                elements.push(this.pdfResultatForValgtKontrolpunkt.nativeElement);
            }
            if (this.pdfPlumeConcentrationPlotDiv && !this.pdfPlumeConcentrationPlotDiv.nativeElement.hidden) {
                elements.push(this.pdfPlumeConcentrationPlotDiv.nativeElement);
            }
            if (this.pdfPlumeConcentrationAtPoCPlotDiv && !this.pdfPlumeConcentrationAtPoCPlotDiv.nativeElement.hidden) {
                elements.push(this.pdfPlumeConcentrationAtPoCPlotDiv.nativeElement);
            }
            if (this.pdfConcentrationPlotDiv && !this.pdfConcentrationPlotDiv.nativeElement.hidden) {
                elements.push(this.pdfConcentrationPlotDiv.nativeElement);
            }
            if (this.pdfAquiferTopConcentrationPlotDiv && !this.pdfAquiferTopConcentrationPlotDiv.nativeElement.hidden) {
                elements.push(this.pdfAquiferTopConcentrationPlotDiv.nativeElement);
            }

            const tasks = [];
            win.scrollTo(0, 0);
            // tslint:disable-next-line: prefer-for-of
            for (let i = 0; i < elements.length; i++) {
                const task = win.html2canvas(elements[i]).then((canvas) => {
                    this.pdfmake.addText('', 'seperateLine');
                    const data = canvas.toDataURL();
                    if (data !== 'data:,') {
                        this.pdfmake.addImageData(data, 480, null);
                    }
                });
                tasks.push(task);
            }
            Promise.all(tasks).then(value => {
                const dismissModel = $('#dismissParameterListViewButton');
                dismissModel.click();
                $('#modalParameterListView').css('opacity', '');

                this.pdfmake.addText('Kommentarer', 'labelHeader');
                for (const comment of this.calculation.comments) {
                    let label = 'Note';
                    if (comment.category !== CommentCategory.None) {
                        label = CommentCategoryHeaderText.get(comment.category);
                    }
                    this.pdfmake.addText(label + ':', 'commentLabel');
                    this.pdfmake.addText(comment.text, 'commentText');
                }

                const fileName = 'Resultater_' + format(new Date(), 'yyyyMMddHHmmss') + '.pdf';
                setTimeout(() => this.pdfmake.download(fileName), 200);
            });
        }, 600);
    }

    public downloadCSV(): void {
        const fileName = 'Resultater_' + format(new Date(), 'yyyyMMddHHmmss') + '.csv';
        const csvData = this.createCsvData();
        this.csvService.export(csvData.fieldDefs, csvData.data, csvData.ret, fileName);
    }

    public createCsvData(): any {
        return CsvDataHelper.createData(
            this.calculation,
            this.calculationInput,
            this.degradationCompoundIndices,
            this.plotPointResult,
            this.plotPointDist
        );
    }

    public displayAsteriskMark(qualityCriterionTypes: QualityCriterionType[], index: number = 0): string {
        const asteriskMark = '(*)';
        const emptyString = '';
        if (!qualityCriterionTypes || !qualityCriterionTypes.length) {
            return emptyString;
        }
        return qualityCriterionTypes[index] === QualityCriterionType.Fictitious ? asteriskMark : emptyString;
    }
}
